// Single source of truth for the tag rule editor:
// - the Trigger type dropdown options (label + description per option),
// - the fields the RuleRow renders on the right side,
// - the on-the-wire payload keys (matching the backend Joi schema in
//   apps/back/src/api/joi/agenticSchemas.js and the Mongoose TagRuleSchema
//   in apps/back/src/api/models/agentic/sharedSchemas.js).
//
// Field types:
//   'tool'      — autocomplete sourced from the agent's attached tools
//   'skill'     — autocomplete sourced from the agent's attached skills
//   'speaker'   — User | Agent | System select
//   'threshold' — numeric operator (>, <, =, >=, <=) + number input
//   'metadata'  — key + operator (full op list) + value
//   'text'      — free text input

export const TAG_TRIGGER_TYPES = [
  'tool_called',
  'tool_not_called',
  'tool_succeeded',
  'tool_not_succeeded',
  'tool_failed',
  'tool_not_failed',
  'call_forwarded',
  'call_not_forwarded',
  'ended_by',
  'not_ended_by',
  'num_interrupts',
  'num_turns',
  'call_duration',
  'metadata',
  'skill_attached',
  'skill_not_attached',
  'skill_detached',
  'skill_not_detached',
  'transcript_contains',
  'transcript_not_contains',
];

export const TAG_RULE_SPEAKERS = ['user', 'agent', 'system'];

export const TAG_RULE_OPERATORS = [
  'eq',
  'neq',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'is_empty',
  'is_not_empty',
  'gt',
  'lt',
  'gte',
  'lte',
];

export const TAG_NUMERIC_OPERATORS = ['gt', 'lt', 'eq', 'gte', 'lte'];

export const NUMERIC_OPERATOR_SYMBOL = {
  gt: '>',
  lt: '<',
  eq: '=',
  gte: '>=',
  lte: '<=',
};

const tool = { kind: 'tool', payloadKey: 'toolName' };
const skill = { kind: 'skill', payloadKey: 'skillName' };
const speaker = { kind: 'speaker', payloadKey: 'speaker' };
const threshold = (unit = 'count') => ({
  kind: 'threshold',
  payloadKey: 'threshold',
  unit,
});
const metadata = { kind: 'metadata', payloadKey: 'metadata' };
const text = { kind: 'text', payloadKey: 'text' };

const TRIGGER_SPECS = {
  tool_called: {
    labelKey: 'TagsTriggerToolCalled',
    descriptionKey: 'TagsTriggerToolCalledHelper',
    fields: [tool],
  },
  tool_not_called: {
    labelKey: 'TagsTriggerToolNotCalled',
    descriptionKey: 'TagsTriggerToolNotCalledHelper',
    fields: [tool],
  },
  tool_succeeded: {
    labelKey: 'TagsTriggerToolSucceeded',
    descriptionKey: 'TagsTriggerToolSucceededHelper',
    fields: [tool],
  },
  tool_not_succeeded: {
    labelKey: 'TagsTriggerToolNotSucceeded',
    descriptionKey: 'TagsTriggerToolNotSucceededHelper',
    fields: [tool],
  },
  tool_failed: {
    labelKey: 'TagsTriggerToolFailed',
    descriptionKey: 'TagsTriggerToolFailedHelper',
    fields: [tool],
  },
  tool_not_failed: {
    labelKey: 'TagsTriggerToolNotFailed',
    descriptionKey: 'TagsTriggerToolNotFailedHelper',
    fields: [tool],
  },
  call_forwarded: {
    labelKey: 'TagsTriggerCallForwarded',
    descriptionKey: 'TagsTriggerCallForwardedHelper',
    fields: [],
  },
  call_not_forwarded: {
    labelKey: 'TagsTriggerCallNotForwarded',
    descriptionKey: 'TagsTriggerCallNotForwardedHelper',
    fields: [],
  },
  ended_by: {
    labelKey: 'TagsTriggerEndedBy',
    descriptionKey: 'TagsTriggerEndedByHelper',
    fields: [speaker],
  },
  not_ended_by: {
    labelKey: 'TagsTriggerNotEndedBy',
    descriptionKey: 'TagsTriggerNotEndedByHelper',
    fields: [speaker],
  },
  num_interrupts: {
    labelKey: 'TagsTriggerNumInterrupts',
    descriptionKey: 'TagsTriggerNumInterruptsHelper',
    fields: [threshold('count')],
  },
  num_turns: {
    labelKey: 'TagsTriggerNumTurns',
    descriptionKey: 'TagsTriggerNumTurnsHelper',
    fields: [threshold('count')],
  },
  call_duration: {
    labelKey: 'TagsTriggerCallDuration',
    descriptionKey: 'TagsTriggerCallDurationHelper',
    fields: [threshold('seconds')],
  },
  metadata: {
    labelKey: 'TagsTriggerMetadata',
    descriptionKey: 'TagsTriggerMetadataHelper',
    fields: [metadata],
  },
  skill_attached: {
    labelKey: 'TagsTriggerSkillAttached',
    descriptionKey: 'TagsTriggerSkillAttachedHelper',
    fields: [skill],
  },
  skill_not_attached: {
    labelKey: 'TagsTriggerSkillNotAttached',
    descriptionKey: 'TagsTriggerSkillNotAttachedHelper',
    fields: [skill],
  },
  skill_detached: {
    labelKey: 'TagsTriggerSkillDetached',
    descriptionKey: 'TagsTriggerSkillDetachedHelper',
    fields: [skill],
  },
  skill_not_detached: {
    labelKey: 'TagsTriggerSkillNotDetached',
    descriptionKey: 'TagsTriggerSkillNotDetachedHelper',
    fields: [skill],
  },
  transcript_contains: {
    labelKey: 'TagsTriggerTranscriptContains',
    descriptionKey: 'TagsTriggerTranscriptContainsHelper',
    fields: [speaker, text],
  },
  transcript_not_contains: {
    labelKey: 'TagsTriggerTranscriptNotContains',
    descriptionKey: 'TagsTriggerTranscriptNotContainsHelper',
    fields: [speaker, text],
  },
};

export const getTriggerSpec = triggerType => TRIGGER_SPECS[triggerType] || null;

export const TRIGGER_TYPE_OPTIONS = TAG_TRIGGER_TYPES.map(triggerType => ({
  value: triggerType,
  ...TRIGGER_SPECS[triggerType],
}));

export default TRIGGER_SPECS;
